
import React from "react";
import moment from "moment";
import withCandidate from "./withCandidate";

const TemplateBody = ({ candidate }) => {
  const languages = candidate?.languages || [];
  const skills = candidate?.skills || [];
  const work = candidate?.workExperiences || [];
  const educations = candidate?.educations || [];
  const references = candidate?.references || [];

  // Petit helper date (prend en charge vide)
  const formatRange = (start, end, fmt = "MMM YYYY") => {
    const s = start ? moment(start).format(fmt) : "";
    const e = end ? moment(end).format(fmt) : "Aujourd’hui";
    return s && e ? `${s} — ${e}` : s || e || "";
  };

  return (
    <>
      <style>{`
        /* =========================================================
           THEMES via .template-page[data-theme="..."]
        ========================================================== */
        .template-page {
          /* Fallback par défaut */
          --accent: #2379B8;
          --ink: #0f172a;
          --muted: #6b7280;
          --paper: #ffffff;
          --panel: #f2f6f9; /* arrière-plan doux pour zones pâles */
          --chip: #e8eef6;
        }

        /* Blue Pro (exactement comme demandé) */
        .template-page[data-theme="blue-pro"] {
          --accent: #2379B8;
          --ink: #0f172a;
          --muted: #6b7280;
          --paper: #ffffff;
          --panel: #f2f6f9;
          --chip: #e8eef6;
        }

        /* Quelques thèmes additionnels (optionnels) */
        .template-page[data-theme="slate"] {
          --accent: #64748b;
          --ink: #0f172a;
          --muted: #6b7280;
          --paper: #ffffff;
          --panel: #f3f4f6;
          --chip: #e5e7eb;
        }
        .template-page[data-theme="emerald"] {
          --accent: #10b981;
          --ink: #0f172a;
          --muted: #5f6b67;
          --paper: #ffffff;
          --panel: #eefcf6;
          --chip: #d7f3ea;
        }
        .template-page[data-theme="royal"] {
          --accent: #7c3aed;
          --ink: #1e1b4b;
          --muted: #6b6b9b;
          --paper: #ffffff;
          --panel: #f5f1ff;
          --chip: #ebe3ff;
        }
        .template-page[data-theme="noir-or"] {
          --accent: #d4a017;
          --ink: #e5e5e5;
          --muted: #bfbfbf;
          --paper: #1b1b1b;
          --panel: #111111;
          --chip: #2a2a2a;
        }
        .template-page[data-theme="malibaara"] {
          --accent: #2379B8;
          --ink: #0f172a;
          --muted: #6b7280;
          --paper: #ffffff;
          --panel: #f2f6f9;
          --chip: #e8eef6;
        }          
        /* Thèmes manquants à ajouter (extraits du 2e template) */
        .template-page[data-theme="bordeaux"] {
          --accent: #b91c1c;
          --ink: #1f1f1f;
          --muted: #7a6565;
          --paper: #ffffff;
          --panel: #2b0d0d;
          --chip: #451414;
        }

        .template-page[data-theme="bleu-clair"] {
          --accent: #3b82f6;
          --ink: #1e293b;
          --muted: #64748b;
          --paper: #ffffff;
          --panel: #0f172a;
          --chip: #1e3a8a;
        }

        .template-page[data-theme="orange"] {
          --accent: #f97316;
          --ink: #0f172a;
          --muted: #6b7280;
          --paper: #ffffff;
          --panel: #2a1505;
          --chip: #4a2c11;
        }

        .template-page[data-theme="teal"] {
          --accent: #0ea5a4;
          --ink: #0b2530;
          --muted: #5b7a85;
          --paper: #ffffff;
          --panel: #07333c;
          --chip: #0a4650;
        }

        .template-page[data-theme="forest"] {
          --accent: #22c55e;
          --ink: #10231b;
          --muted: #5f776e;
          --paper: #ffffff;
          --panel: #091c14;
          --chip: #113d2c;
        }

        .template-page[data-theme="rose"] {
          --accent: #e11d48;
          --ink: #2b0a13;
          --muted: #a36a7a;
          --paper: #ffffff;
          --panel: #1a0b11;
          --chip: #3a0f1c;
        }

        .template-page[data-theme="indigo"] {
          --accent: #4f46e5;
          --ink: #0f172a;
          --muted: #717ab9;
          --paper: #ffffff;
          --panel: #12163a;
          --chip: #212a6b;
        }


        /* =========================================================
           LAYOUT GLOBAL (inspiré de ta maquette)
        ========================================================== */
        .tpl5 {
          width: 210mm;
          min-height: 297mm;
          margin: 10px 0; /* laisse withCandidate gérer le décalage */
          background: var(--paper);
          color: var(--ink);
          font-family: Arial, Helvetica, sans-serif;
          box-shadow: 0 10px 30px rgba(0,0,0,.12);
        }

        body { background:#222; }

        /* -------- HEADER BANDE -------- */
        .tpl5 .hdr {
          background: linear-gradient(180deg, var(--accent) 0%, rgba(0,0,0,0.05) 100%), var(--accent);
          color: #fff;
          padding: 22px 28px 48px; /* espace bas pour l'avatar qui chevauche */
          position: relative;
          border-bottom: 1px solid rgba(255,255,255,.25);
        }

        .tpl5 .hdr-inner {
          display: grid;
          grid-template-columns: 1.2fr 1fr; /* gauche: identité, droite: contacts */
          gap: 16px;
          align-items: end;
        }

        .tpl5 .id-block .name {
          font-size: 28px;
          font-weight: 900;
          letter-spacing: .3px;
        }
        .tpl5 .id-block .title {
          margin-top: 4px;
          font-size: 14px;
          opacity: .95;
        }

        /* Contacts à droite */
        .tpl5 .contacts {
          display: grid;
          gap: 6px;
          justify-content: end;
        }
        .tpl5 .c-item {
          display: grid;
          grid-template-columns: 18px 1fr;
          gap: 8px;
          align-items: center;
          font-size: 13px;
          opacity: .95;
          color: #eef6ff;
          word-break: break-all;
        }
        .tpl5 .c-item a { color: inherit; text-decoration: none; }
        .tpl5 .c-item svg { display:block; }

        /* Avatar chevauchant le bas du header */
        .tpl5 .avatar-wrap {
          position: absolute;
          left: 28px;
          bottom: -48px; /* chevauche */
          width: 110px;
          height: 110px;
          border-radius: 50%;
          overflow: hidden;
          border: 4px solid var(--paper);
          background: #fff;
          box-shadow: 0 8px 20px rgba(0,0,0,.15);
        }
        .tpl5 .avatar-wrap img {
          width: 100%; height: 100%; object-fit: cover; display:block;
        }

        /* -------- CORPS -------- */
        .tpl5 .content {
          padding: 60px 28px 28px; /* top compensé pour l’avatar */
        }

        .tpl5 .grid {
          display: grid;
          grid-template-columns: 1.6fr 1fr; /* gauche large, droite étroite */
          gap: 24px;
        }

        /* Titres de section avec ligne à droite (style rapport) */
        .tpl5 .sec-title {
          display: grid;
          grid-template-columns: auto 1fr;
          align-items: center;
          gap: 10px;
          color: var(--accent);
          font-weight: 800;
          font-size: 12px;
          letter-spacing: .8px;
          text-transform: uppercase;
          margin: 14px 0 10px;
        }
        .tpl5 .sec-title:after {
          content: "";
          height: 2px;
          background: var(--accent);
          opacity: .35;
          border-radius: 4px;
        }

        /* Paragraphes */
        .tpl5 p { font-size: 13px; color: #374151; margin: 6px 0 0; }

        /* -------- TIMELINE EXP -------- */
        .tpl5 .timeline { position: relative; padding-left: 20px; }
        .tpl5 .timeline:before {
          content:""; position:absolute; left:8px; top:2px; bottom:2px; width:2px; background:#cfe3f7;
        }
        .tpl5 .t-item { position: relative; margin: 0 0 14px 0; padding-left: 14px; }
        .tpl5 .dot {
          position:absolute; left:-16px; top:3px; width:10px; height:10px; border-radius:50%; background: var(--accent);
        }
        .tpl5 .t-row { display:flex; justify-content:space-between; gap: 12px; font-weight: 800; line-height: 1.2; }
        .tpl5 .t-sub { color: var(--muted); font-size: 12px; white-space: nowrap; }

        .tpl5 ul { margin: 6px 0 0 18px; padding:0; }
        .tpl5 li { font-size: 13px; color: #374151; margin: 2px 0; }

        /* -------- COLONNE DROITE -------- */
        .tpl5 .panel {
          background: var(--panel);
          border: 1px solid rgba(0,0,0,.06);
          border-radius: 8px;
          padding: 12px 14px;
        }
        .tpl5 .chips { display:flex; flex-wrap:wrap; gap: 6px; }
        .tpl5 .chip {
          background: var(--chip);
          border-radius: 999px;
          font-size: 11px;
          padding: 5px 9px;
          color: #0f2640;
        }

        /* Liste langues compacte */
        .tpl5 .lang-list { display: grid; gap: 6px; font-size: 12px; }
        .tpl5 .lang-item { line-height: 1.35; }
        .tpl5 .lang-name { font-weight: 700; color: #0b1f36; }
        .tpl5 .lang-meta { color: #44566c; }
        .tpl5 .lang-sep { margin: 0 6px; opacity: .6; }

        /* Éducation (ligne subtile) */
        .tpl5 .edu .t-item { margin-bottom: 10px; }
      `}</style>

      <div className="wrap tpl5">
        {/* ================= HEADER ================= */}
        <div className="hdr">
          <div className="hdr-inner">
            <div className="id-block">
              <div className="name">{candidate?.firstName} {candidate?.lastName}</div>
              <div className="title">{candidate?.targetTitle || ""}</div>
            </div>

            {/* Contacts à droite (affichés seulement si fournis) */}
            <div className="contacts">
              {candidate?.email && (
                <div className="c-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M3 7.5l9 6 9-6" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="3" y="5" width="18" height="14" rx="2.2" stroke="#fff" strokeWidth="1.6"/>
                  </svg>
                  <a href={`mailto:${candidate.email}`}>{candidate.email}</a>
                </div>
              )}
              {candidate?.phoneNumber1 && (
                <div className="c-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M5 4h3l2 5-2 1a12 12 0 0 0 6 6l1.1-2.1 5.9 2.1v3a2 2 0 0 1-2.2 2A17 17 0 0 1 3 6.2 2 2 0 0 1 5 4Z" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {candidate.phoneNumber1}
                </div>
              )}
              {candidate?.linkedinUrl && (
                <div className="c-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M4 4h4v16H4V4Zm6 6h4v2.4h.06A4.36 4.36 0 0 1 18 8.3c3.06 0 4 1.9 4 5.1V20h-4v-5.2c0-1.2 0-2.8-1.7-2.8-1.7 0-2 1.3-2 2.7V20h-4V10Z" fill="#fff"/>
                  </svg>
                  <a href={candidate.linkedinUrl} target="_blank" rel="noreferrer">{candidate.linkedinUrl}</a>
                </div>
              )}
              {candidate?.githubUrl && (
                <div className="c-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2a10 10 0 0 0-3.16 19.49c.5.1.68-.22.68-.48v-1.7c-2.78.6-3.37-1.2-3.37-1.2-.46-1.16-1.12-1.47-1.12-1.47-.92-.64.07-.63.07-.63 1.02.07 1.56 1.06 1.56 1.06.9 1.56 2.36 1.12 2.94.85.1-.66.35-1.12.64-1.38-2.22-.25-4.56-1.12-4.56-5 0-1.12.39-2.04 1.04-2.76-.1-.25-.46-1.28.1-2.66 0 0 .86-.28 2.82 1.06A9.7 9.7 0 0 1 12 6.8c.86 0 1.74.12 2.56.36 1.96-1.34 2.82-1.06 2.82-1.06.56 1.38.2 2.41.1 2.66.65.72 1.04 1.64 1.04 2.76 0 3.9-2.34 4.74-4.58 4.98.36.32.68.94.68 1.9v2.8c0 .26.18.58.68.48A10 10 0 0 0 12 2Z" fill="#fff"/>
                  </svg>
                  <a href={candidate.githubUrl} target="_blank" rel="noreferrer">{candidate.githubUrl}</a>
                </div>
              )}
              {candidate?.website && (
                <div className="c-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z" stroke="#fff" strokeWidth="1.6"/>
                    <path d="M3 12h18M12 3c3.5 3.5 3.5 14.5 0 18M12 3c-3.5 3.5-3.5 14.5 0 18" stroke="#fff" strokeWidth="1.2" strokeLinecap="round"/>
                  </svg>
                  <a href={candidate.website} target="_blank" rel="noreferrer">{candidate.website}</a>
                </div>
              )}
            </div>

          </div>

          {/* Avatar flottant */}
          <div className="avatar-wrap">
            <img
              crossOrigin="anonymous"
              src={candidate?.profilePictureUrl || "/assets/images/avatar/avatar2.png"}
              alt=""
            />
          </div>
        </div>

        {/* ================= CONTENT ================= */}
        <div className="content">
          <div className="grid">

            {/* ===== Colonne gauche ===== */}
            <div className="left">

              {/* Career Summary / Profil */}
              <div className="sec-title">Career Summary</div>
              <div
                className="summary"
                dangerouslySetInnerHTML={{ __html: candidate?.summaryDescription || "" }}
              />

              {/* Work Experience */}
              {!!work?.length && (
                <>
                  <div className="sec-title">Work Experience</div>
                  <div className="timeline">
                    {work.map((job, i) => (
                      <div className="t-item" key={i}>
                        <span className="dot" />
                        <div className="t-row">
                          <div>{job?.title}</div>
                          <div className="t-sub">{formatRange(job?.startDate, job?.endDate)}</div>
                        </div>
                        {job?.company && <div className="t-sub">{job.company}</div>}
                        {job?.description && <p>{job.description}</p>}

                        {/* Achievements (si disponible sous forme de tableau) */}
                        {Array.isArray(job?.achievements) && job.achievements.length > 0 && (
                          <ul>
                            {job.achievements.map((a, idx) => <li key={idx}>{a}</li>)}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Education */}
              {!!educations?.length && (
                <>
                  <div className="sec-title">Education</div>
                  <div className="timeline edu">
                    {educations.map((edu, i) => (
                      <div className="t-item" key={i}>
                        <span className="dot" />
                        <div className="t-row">
                          <div>{edu?.degree}</div>
                          <div className="t-sub">
                            {edu?.startYear} — {edu?.endYear}
                          </div>
                        </div>
                        {edu?.institution && <div className="t-sub">{edu.institution}</div>}
                        {edu?.description && <p>{edu.description}</p>}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* REFERENCE */}
              {!!references?.length && (
                <>
                  <div className="sec-title">Références</div>
                  <div className="timeline edu">
                    {references.map((ref, i) => (
                      <div className="t-item" key={i}>
                        <span className="dot" />
                        <div className="t-row">
                          <div>{ref?.fullName}</div>
                          <div className="t-sub">
                            {ref?.title}
                          </div>
                        </div>
                        <p>
                          Tel: {ref.phoneNumber1} / {ref.phoneNumber2} <br />
                          Email: {ref.email}
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              )}

            </div>

            {/* ===== Colonne droite ===== */}
            <div className="right">

              {/* Skills & Tools */}
              {!!skills?.length && (
                <div className="panel">
                  <div className="sec-title">Skills &amp; Tools</div>
                  <div className="chips">
                    {skills.map((s, i) => (
                      <span className="chip" key={i}>{s?.title}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Languages */}
              {!!languages?.length && (
                <div className="panel" style={{ marginTop: 14 }}>
                  <div className="sec-title">Languages</div>
                  <div className="lang-list">
                    {languages.map((l, i) => {
                      const parts = [];
                      if (l?.percentage) parts.push(`${l.percentage}%`);
                      if (l?.writingLevel) parts.push(`Écrit : ${l.writingLevel}`);
                      if (l?.speakingLevel) parts.push(`Parlé : ${l.speakingLevel}`);
                      const meta = parts.join(" • ");

                      return (
                        <div className="lang-item" key={i}>
                          <span className="lang-name">{l?.name}</span>
                          {meta && (
                            <>
                              <span className="lang-sep">—</span>
                              <span className="lang-meta">{meta}</span>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default withCandidate(TemplateBody, "Simple Une Colonne");
